/**
 * Forge Coach conversation state. Loads the dossier + history on mount, then
 * drives the SSE stream on send(): buffers content_delta into streamingText,
 * applies dossier_update / phase_change live, and finalizes the assistant
 * message on completion.
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CoachDossier,
  getCoachDossier,
  getCoachMessages,
} from "../api/client";
import { CoachRateLimitError, streamCoachMessage } from "../api/coach";

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

export interface UseCoachChat {
  messages: ChatMessage[];
  dossier: CoachDossier | null;
  phase: string;
  streamingText: string;
  sending: boolean;
  loading: boolean;
  error: string | null;
  send: (text: string) => Promise<void>;
}

export function useCoachChat(): UseCoachChat {
  // A hook (not a component), so it may call other hooks directly — no
  // TFunction-parameter indirection needed (contrast src/utils/heroCtas.ts,
  // a plain function, I18N_PLAN.md Phase 2). ev.message / e.message below
  // are dynamic server-originated content (SSE stream / rate-limit errors),
  // not static UI copy — left as-is.
  const { t } = useTranslation("coach");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [dossier, setDossier] = useState<CoachDossier | null>(null);
  const [phase, setPhase] = useState<string>("intake");
  const [streamingText, setStreamingText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    Promise.all([getCoachDossier(), getCoachMessages()])
      .then(([d, msgs]) => {
        if (!active) return;
        setDossier(d);
        setPhase(d.current_phase);
        setMessages(
          msgs.map((m) => ({
            role: m.role === "assistant" ? "assistant" : "user",
            content: m.content,
          }))
        );
      })
      .catch(() => {
        if (active) setError(t("useCoachChat.loadFailed"));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [t]);

  const send = useCallback(async (text: string) => {
    setError(null);
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setSending(true);
    let assembled = "";
    try {
      await streamCoachMessage(text, (ev) => {
        switch (ev.type) {
          case "content_delta":
            assembled += ev.text;
            setStreamingText(assembled);
            break;
          case "dossier_update":
            setDossier((prev) =>
              prev ? { ...prev, [ev.path]: ev.value } : prev
            );
            break;
          case "phase_change":
            setPhase(ev.to);
            setDossier((prev) =>
              prev ? { ...prev, current_phase: ev.to } : prev
            );
            break;
          case "error":
            setError(ev.message);
            break;
          case "message_start":
          case "message_complete":
            break;
        }
      });
      if (assembled) {
        setMessages((prev) => [...prev, { role: "assistant", content: assembled }]);
      }
    } catch (e) {
      if (e instanceof CoachRateLimitError) {
        setError(e.message);
      } else {
        setError(t("useCoachChat.interrupted"));
      }
      if (assembled) {
        setMessages((prev) => [...prev, { role: "assistant", content: assembled }]);
      }
    } finally {
      setStreamingText("");
      setSending(false);
    }
  }, [t]);

  return { messages, dossier, phase, streamingText, sending, loading, error, send };
}
